<template>
  <!-- KArs 23.09.2026 Teil 2, IV: Hinweis, sobald eine neue KARS-Version
       bereitsteht. Ohne ihn sieht ein seit Tagen offener Tab die Neuerungen
       nie („Ich kann die Neuerungen bei mir nicht sehen"). -->
  <Transition name="app-update">
    <div
      v-if="updateAvailable"
      class="app-update"
      data-testid="app-update-banner"
      role="status"
    >
      <RefreshCw :size="16" aria-hidden="true" />
      <span class="app-update__text">
        <strong>{{ $gt('Neue KARS-Version verfügbar.') }}</strong>
        {{ $gt('Laden Sie die Seite neu, um die Neuerungen zu sehen.') }}
      </span>
      <button class="app-update__btn" type="button" @click="reload">{{ $gt('Jetzt neu laden') }}</button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
  import { RefreshCw } from 'lucide-vue-next'
  import { onBeforeUnmount, onMounted, ref } from 'vue'
  import { currentEntryScript, entryScriptFromHtml, isNewerVersion } from '@/utils/appVersion'

  // Alle fünf Minuten und beim Zurückkehren in den Tab prüfen.
  const CHECK_INTERVAL_MS = 5 * 60 * 1000

  const updateAvailable = ref(false)
  const loadedEntry = currentEntryScript()
  let timer: ReturnType<typeof setInterval> | null = null

  async function check () {
    if (!loadedEntry || updateAvailable.value) return
    try {
      const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' })
      if (!res.ok) return
      updateAvailable.value = isNewerVersion(loadedEntry, entryScriptFromHtml(await res.text()))
    } catch {
      // Offline oder Server kurz weg (z. B. während eines Deploys): nächste Runde.
    }
  }

  function onVisibility () {
    if (document.visibilityState === 'visible') check()
  }

  function reload () {
    window.location.reload()
  }

  onMounted(() => {
    // Im Entwicklungsmodus gibt es keine Hash-Dateien; loadedEntry ist null.
    if (!loadedEntry) return
    check()
    timer = setInterval(check, CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisibility)
  })

  onBeforeUnmount(() => {
    if (timer) clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisibility)
  })
</script>

<style scoped>
.app-update {
  position: fixed; left: 50%; bottom: 20px; z-index: 9000;
  transform: translateX(-50%);
  display: flex; align-items: center; gap: 12px;
  max-width: calc(100vw - 32px);
  padding: 10px 12px 10px 16px; border-radius: 12px;
  background: #0f172a; color: #e2e8f0;
  box-shadow: 0 12px 32px rgba(15, 23, 42, .28), 0 2px 6px rgba(15, 23, 42, .18);
  font-size: 0.82rem; line-height: 1.4;
}
.app-update__text strong { color: #fff; font-weight: 700; margin-right: 4px; }
.app-update__btn {
  flex-shrink: 0; padding: 6px 14px; border-radius: 8px; cursor: pointer;
  border: none; background: #f97316; color: #fff;
  font-size: 0.78rem; font-weight: 700;
  transition: background .15s, transform .15s;
}
.app-update__btn:hover { background: #ea580c; }
.app-update__btn:active { transform: translateY(1px); }
.app-update__btn:focus-visible { outline: 2px solid #fdba74; outline-offset: 2px; }
.app-update-enter-active, .app-update-leave-active { transition: opacity .25s, transform .25s; }
.app-update-enter-from, .app-update-leave-to { opacity: 0; transform: translate(-50%, 12px); }
@media (prefers-reduced-motion: reduce) {
  .app-update-enter-active, .app-update-leave-active { transition: none; }
}
@media (max-width: 600px) {
  .app-update { flex-wrap: wrap; bottom: 12px; }
}
</style>
